import React, { useState, useRef, useEffect } from 'react';
import { ProgressBar } from 'react-bootstrap';
import './custom-progress-bar.css'; // Importamos los estilos personalizados

/**
 * Componente de barra de progreso estándar y unificado
 * ✅ SIEMPRE VISIBLE - Sin renderizado condicional
 * ✅ UNA SOLA BARRA - Sin múltiples ProgressBar anidados
 * ✅ SIMPLE Y DIRECTO - Solo muestra el progreso sin complicaciones
 * ✅ AUTO-OCULTAR - Se oculta automáticamente después de completarse
 * ✅ ESTILOS PERSONALIZADOS - Usa estilos definidos en custom-progress-bar.css
 * ✅ ESTADO INACTIVO - No se muestra hasta que haya actividad real
 * ✅ DETECCIÓN DE RESET - Reinicia a 0% en nuevas búsquedas
 * ✅ OPTIMIZACIÓN DE CPU - Detiene verificaciones después del 100%
 */
const StandardProgressBar = ({ 
    calculateTotalProgress, 
    debugMetrics,
    hideDelay = 1000 // Tiempo en ms para ocultar la barra después de completarse (default: 1 segundos)
}) => {
    // Estado para el progreso actual
    const [currentProgress, setCurrentProgress] = useState(0);
    
    // Estado para controlar la visibilidad del componente
    const [visible, setVisible] = useState(true);
    
    // Estado para determinar si hay alguna actividad en curso
    const [isActive, setIsActive] = useState(false);
    
    // Referencias para control de progreso y evitar regresiones
    const maxProgressRef = useRef(0);
    const lastSearchStartTimeRef = useRef(null);
    const currentProgressRef = useRef(0);
    const completionTimerRef = useRef(null);
    const lastConnectionStatesRef = useRef({});
    
    // Verificar si hay actividad real basada en las conexiones
    useEffect(() => {
        if (!debugMetrics || !debugMetrics.connections) return;
        
        // Comprobar si hay alguna conexión activa
        const anyActiveConnection = Object.values(debugMetrics.connections).some(conn => 
            conn.status === 'downloading' || 
            (conn.status === 'completed' && conn.downloadProgress < 100) || 
            (conn.startTime && !conn.endTime) ||
            (conn.downloadProgress > 0 && conn.downloadProgress < 100)
        );
        
        // Comprobar si hay tiempo de búsqueda registrado
        const hasSearchTime = !!debugMetrics.searchStartTime;
        
        // Establecer estado activo si hay alguna conexión activa o tiempo de búsqueda
        setIsActive(anyActiveConnection || hasSearchTime);
        
    }, [debugMetrics]);
    
    // Reiniciar el estado cuando cambian los datos de depuración o la función de cálculo
    useEffect(() => {
        // Si hay un nuevo conjunto de datos de depuración o una nueva función de cálculo,
        // reiniciamos completamente el estado de la barra de progreso
        //console.log('🔄 [StandardProgressBar] Reinicio por cambio de props');
        maxProgressRef.current = 0;
        currentProgressRef.current = 0;
        setCurrentProgress(0);
        setVisible(true);
        
        // Limpiar cualquier temporizador pendiente
        if (completionTimerRef.current) {
            clearTimeout(completionTimerRef.current);
            completionTimerRef.current = null;
        }
        
        // Actualizar la referencia del tiempo de búsqueda
        if (debugMetrics?.searchStartTime) {
            lastSearchStartTimeRef.current = debugMetrics.searchStartTime;
        }
    }, [calculateTotalProgress, debugMetrics?.searchStartTime]); // Incluimos searchStartTime como dependencia
    
    // Detectar cambios en las conexiones para garantizar un progreso coherente
    useEffect(() => {
        if (!debugMetrics || !debugMetrics.connections) return;
        
        const connectionStates = {};
        let resetNeeded = false;
        
        // Verificar si alguna conexión se ha reiniciado
        Object.entries(debugMetrics.connections).forEach(([connId, conn]) => {
            // Capturar el estado actual de cada conexión
            connectionStates[connId] = {
                startTime: conn.startTime,
                status: conn.status,
                progress: conn.downloadProgress || 0
            };
            
            // Verificar si esta conexión se ha reiniciado desde la última verificación
            const lastState = lastConnectionStatesRef.current[connId];
            if (lastState && conn.startTime && lastState.startTime !== conn.startTime) {
                //console.log(`🔄 [StandardProgressBar] Conexión ${connId} reiniciada`);
                resetNeeded = true;
            }
        });
        
        // Si alguna conexión se reinició, reseteamos el progreso
        if (resetNeeded) {
            //console.log('🔄 [StandardProgressBar] Reset detectado en conexiones. Reiniciando a 0.');
            maxProgressRef.current = 0;
            setCurrentProgress(0);
            setVisible(true);
        }
        
        // Actualizar la referencia para la próxima verificación
        lastConnectionStatesRef.current = connectionStates;
    }, [debugMetrics]);
    
    // ✅ USAR useEffect PARA CONFIGURAR UN INTERVALO DE ACTUALIZACIÓN
    useEffect(() => {
        // Si no hay actividad, no necesitamos verificar el progreso
        if (!isActive) return;
        
        // Referencia para controlar el intervalo
        let intervalRef = null;
        
        // Función que verifica y actualiza el progreso
        const checkProgress = () => {
            // Verificar si hay una función de cálculo
            if (!calculateTotalProgress || typeof calculateTotalProgress !== 'function') {
                return;
            }
            
            // Si ya alcanzamos el 100%, detener el intervalo
            if (currentProgressRef.current >= 100) {
                //console.log('🛑 [StandardProgressBar] Progreso completado (100%), deteniendo intervalo.');
                if (intervalRef) {
                    clearInterval(intervalRef);
                    intervalRef = null;
                }
                return;
            }
            
            // Calcular el nuevo progreso
            const newProgress = calculateTotalProgress();
            
            // 🔍 DEBUG: Verificar el progreso calculado (reducido para evitar logs excesivos)
            if (currentProgressRef.current < 100) {
                //console.log('🎯 [StandardProgressBar] Verificando progreso:', {
                //    newProgress: newProgress.toFixed(1),
                //    currentProgress: currentProgressRef.current.toFixed(1),
                //    maxProgress: maxProgressRef.current.toFixed(1),
                //    searchStartTime: debugMetrics?.searchStartTime,
                //    lastSearch: lastSearchStartTimeRef.current,
                //    visible: visible,
                //    isActive: isActive
                //});
            }
            
            // ✅ DETECTAR RESET POR NUEVA BÚSQUEDA
            if (debugMetrics?.searchStartTime && debugMetrics.searchStartTime !== lastSearchStartTimeRef.current) {
                //console.log('🔄 [StandardProgressBar] Reset detectado - nueva búsqueda. Reiniciando a 0.');
                lastSearchStartTimeRef.current = debugMetrics.searchStartTime;
                maxProgressRef.current = 0;
                currentProgressRef.current = 0;
                setCurrentProgress(0);
                setVisible(true); // Asegurar que la barra sea visible para la nueva búsqueda
                
                // Limpiar cualquier temporizador pendiente
                if (completionTimerRef.current) {
                    clearTimeout(completionTimerRef.current);
                    completionTimerRef.current = null;
                }
                
                return;
            }
            
            // ✅ ACTUALIZAR PROGRESO CON ANTI-REGRESIÓN
            // Usar una pequeña tolerancia para evitar problemas de punto flotante
            if (newProgress > maxProgressRef.current - 0.01) {
                if (Math.abs(newProgress - currentProgressRef.current) > 0.01) {
                    maxProgressRef.current = newProgress;
                    currentProgressRef.current = newProgress;
                    setCurrentProgress(newProgress);
                    //console.log(`✅ [StandardProgressBar] Progreso actualizado: ${newProgress.toFixed(1)}%`);
                }
            } else {
                //console.log(`⚠️ [StandardProgressBar] Progreso bloqueado (anti-regresión): nuevo ${newProgress.toFixed(1)}% < max ${maxProgressRef.current.toFixed(1)}%`);
            }
            
            // ✅ DETENER EL INTERVALO CUANDO ALCANZAMOS EL 100% (o muy cerca)
            // Considerar completo si está >= 95% para evitar bloqueos cerca del final
            if (newProgress >= 95) {
                //console.log(`🛑 [StandardProgressBar] Progreso muy cerca de completarse (${newProgress.toFixed(1)}%), forzando al 100% y deteniendo verificaciones.`);
                
                // Asegurarnos de actualizar el progreso exactamente a 100%
                maxProgressRef.current = 100;
                currentProgressRef.current = 100;
                setCurrentProgress(100);
                
                // Programar auto-hide directamente desde aquí también
                if (completionTimerRef.current) {
                    clearTimeout(completionTimerRef.current);
                }
                completionTimerRef.current = setTimeout(() => {
                    setVisible(false);
                    setIsActive(false);
                    //console.log('🚫 [StandardProgressBar] Barra de progreso ocultada después de completarse (desde checkProgress)');
                }, hideDelay);
                
                if (intervalRef) {
                    clearInterval(intervalRef);
                    intervalRef = null;
                }
            }
        };
        
        // Realizar una comprobación inicial
        checkProgress();
        
        // Configurar una comprobación periódica solo si no alcanzamos cerca del 100%
        if (currentProgressRef.current < 95) {
            intervalRef = setInterval(checkProgress, 500);
        }
        
        // Limpiar intervalo al desmontar
        return () => {
            if (intervalRef) {
                clearInterval(intervalRef);
                intervalRef = null;
                //console.log('🧹 [StandardProgressBar] Intervalo de verificación limpiado.');
            }
        };
    }, [calculateTotalProgress, debugMetrics, visible, isActive, hideDelay]); // Incluido hideDelay en las dependencias

    // Sincronizar referencia con el estado y manejar auto-hide
    useEffect(() => {
        currentProgressRef.current = currentProgress;
        
        // Si el progreso alcanza cerca del 100%, configurar un temporizador para ocultar la barra
        if (currentProgress >= 95) {
            //console.log(`🏁 [StandardProgressBar] Progreso cerca de completarse (${currentProgress.toFixed(1)}%). Ocultando en ${hideDelay/1000}s`);
            
            // Forzar a 100% visualmente
            if (currentProgress >= 95 && currentProgress < 100) {
                setCurrentProgress(100);
            }
            
            // Limpiar el temporizador existente si hay uno
            if (completionTimerRef.current) {
                clearTimeout(completionTimerRef.current);
                completionTimerRef.current = null;
            }
            
            // Configurar un nuevo temporizador para ocultar la barra
            completionTimerRef.current = setTimeout(() => {
                setVisible(false);
                setIsActive(false); // También desactivar el estado "activo"
                //console.log('🚫 [StandardProgressBar] Barra de progreso ocultada después de completarse');
            }, hideDelay);

            // Asegurarnos de que el temporizador se limpie si el componente se desmonta
            return () => {
                if (completionTimerRef.current) {
                    clearTimeout(completionTimerRef.current);
                    completionTimerRef.current = null;
                }
            };
        } else if (currentProgress < 100 && !visible) {
            // Si el progreso baja de 100% (nueva búsqueda) y la barra está oculta, mostrarla de nuevo
            setVisible(true);
        }
    }, [currentProgress, hideDelay, visible]); // Incluimos visible para evitar advertencias

    // Efecto de limpieza al desmontar
    useEffect(() => {
        return () => {
            // Limpiar el temporizador de completion si el componente se desmonta
            if (completionTimerRef.current) {
                clearTimeout(completionTimerRef.current);
                completionTimerRef.current = null;
            }
        };
    }, []);

    // ✅ DETERMINAR ESTADO SIMPLE
    const isPending = currentProgress > 0 && currentProgress < 100;
    const isCompleted = currentProgress >= 100;
    
    // ✅ FORMATEAR TIEMPO
    const formatTime = (startTime) => {
        if (!startTime) return "";
        const elapsed = Date.now() - startTime;
        if (elapsed < 60000) return `${Math.round(elapsed/1000)}s`;
        const minutes = Math.floor(elapsed/60000);
        const seconds = Math.round((elapsed%60000)/1000);
        return `${minutes}m ${seconds}s`;
    };

    // Si la barra no debe ser visible, no renderizar nada
    // Condición simplificada para mayor claridad
    if (!visible) {
        //console.log('🚫 [StandardProgressBar] Barra oculta, no renderizando');
        return null;
    }
    
    // Si no hay actividad y no hay progreso, no renderizar
    if (!isActive && currentProgress === 0) {
        //console.log('🚫 [StandardProgressBar] Sin actividad ni progreso, no renderizando');
        return null;
    }

    return (
        <div className={`standard-progress-container mb-3 custom-progress-bar ${isCompleted ? 'completed' : ''}`}>
            {/* ✅ ENCABEZADO SIMPLE */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <span className={`status-text ${
                        isCompleted ? 'text-dark' : 
                        isPending ? 'text-dark' : 'text-dark'
                    }`}>
                        {isCompleted ? 'Completado' : isPending ? 'Generando reporte' : 'Preparando'} 
                        {currentProgress > 0 ? ` ${currentProgress.toFixed(0)}%` : ''}
                    </span>
                </div>
                
                {debugMetrics?.searchStartTime && (
                    <div className="time-text">
                        {formatTime(debugMetrics.searchStartTime)}
                    </div>
                )}
            </div>

            {/* ✅ BARRA DE PROGRESO SIMPLE - SIEMPRE LA MISMA */}
            <ProgressBar
                now={currentProgress}
                variant={isCompleted ? 'danger' : isPending ? 'danger' : 'danger'}
                animated={isPending}
                style={{
                    height: 20,
                    borderRadius: '6px',
                    backgroundColor: '#f5f5f5'
                }}
                label={currentProgress > 0 ? `${currentProgress.toFixed(0)}%` : ''}
            />
            
        </div>
    );
};

export default StandardProgressBar;
